import { useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateInternalNetworkCredentialDto,
  UpdateInternalNetworkCredentialDto,
} from '@terra/shared-contracts-internal-network';
import {
  resolveInternalNetworkCredentialVerificationReadiness,
  shouldPollInternalNetworkCredentialVerificationReadiness,
} from '@terra/ui-design-system';
import { internalNetworkCredentialsApi } from '../../clients/tester-client/internal-network-credentials-api';
import { INTERNAL_NETWORK_OWNED_PRINCIPALS_KEY } from './useInternalNetworkOverview';
import {
  INTERNAL_NETWORK_DISCOVERY_READINESS_KEY,
  useInternalNetworkDiscoveryReadiness,
} from './useInternalNetworkDiscoveryStatus';
import {
  INTERNAL_NETWORK_CREDENTIALS_KEY,
  useInternalNetworkEnrollments,
} from './useSurfaceSettings';

const WAITING_POLL_INTERVAL_MS = 5_000;

const requireNetworkId = (networkId: string | null): string => {
  if (!networkId) {
    throw new Error('Network ID is required');
  }

  return networkId;
};

const useInvalidateInternalNetworkCredentials = (networkId: string | null) => {
  const queryClient = useQueryClient();

  return () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: [INTERNAL_NETWORK_CREDENTIALS_KEY, networkId],
      }),
      queryClient.invalidateQueries({
        queryKey: [INTERNAL_NETWORK_DISCOVERY_READINESS_KEY, networkId],
      }),
    ]);
};

export function useCreateInternalNetworkCredential(networkId: string | null) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateInternalNetworkCredentials(networkId);

  return useMutation({
    mutationFn: (body: CreateInternalNetworkCredentialDto) =>
      internalNetworkCredentialsApi.createInternalNetworkCredential({
        networkId: requireNetworkId(networkId),
        body,
      }),
    onSuccess: () =>
      Promise.all([
        invalidate(),
        queryClient.invalidateQueries({
          queryKey: [INTERNAL_NETWORK_OWNED_PRINCIPALS_KEY, networkId],
        }),
      ]),
  });
}

export function useUpdateInternalNetworkCredential(networkId: string | null) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateInternalNetworkCredentials(networkId);

  return useMutation({
    mutationFn: ({
      credentialId,
      body,
    }: {
      credentialId: string;
      body: UpdateInternalNetworkCredentialDto;
    }) =>
      internalNetworkCredentialsApi.updateInternalNetworkCredential({
        networkId: requireNetworkId(networkId),
        credentialId,
        body,
      }),
    onSuccess: () =>
      Promise.all([
        invalidate(),
        queryClient.invalidateQueries({
          queryKey: [INTERNAL_NETWORK_OWNED_PRINCIPALS_KEY, networkId],
        }),
      ]),
  });
}

export function useDeleteInternalNetworkCredential(networkId: string | null) {
  const invalidate = useInvalidateInternalNetworkCredentials(networkId);

  return useMutation({
    mutationFn: (credentialId: string) =>
      internalNetworkCredentialsApi.deleteInternalNetworkCredential({
        networkId: requireNetworkId(networkId),
        credentialId,
      }),
    onSuccess: () => invalidate(),
  });
}

export function useVerifyInternalNetworkCredential(networkId: string | null) {
  const invalidate = useInvalidateInternalNetworkCredentials(networkId);

  return useMutation({
    mutationFn: (credentialId: string) =>
      internalNetworkCredentialsApi.verifyInternalNetworkCredential({
        networkId: requireNetworkId(networkId),
        credentialId,
      }),
    onSettled: () => invalidate(),
  });
}

export function useInternalNetworkCredentialVerificationReadiness({
  networkId,
  pollWhileWaiting = false,
}: {
  networkId: string | null;
  pollWhileWaiting?: boolean;
}) {
  const enrollmentsQuery = useInternalNetworkEnrollments(networkId);
  const readinessQuery = useInternalNetworkDiscoveryReadiness(networkId);
  const enrollments = enrollmentsQuery.data?.data;
  const { refetch: refetchEnrollments } = enrollmentsQuery;
  const { refetch: refetchReadiness } = readinessQuery;

  const readiness = useMemo(
    () =>
      resolveInternalNetworkCredentialVerificationReadiness({
        enrollments: enrollments ?? [],
        discoveryReadiness: readinessQuery.data,
        readinessSource: 'auto',
      }),
    [enrollments, readinessQuery.data],
  );

  const shouldPoll = shouldPollInternalNetworkCredentialVerificationReadiness({
    pollWhileWaiting,
    readiness,
  });

  useEffect(() => {
    if (!shouldPoll) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void refetchReadiness();
      void refetchEnrollments();
    }, WAITING_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refetchEnrollments, refetchReadiness, shouldPoll]);

  return readiness;
}
